import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, updateUserProfile, getAllCompanies } from '../services/api';

const EmployerProfilePage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    companyId: '',
    customCompanyName: ''
  });

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [useCustomCompany, setUseCustomCompany] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      // Load companies
      const companiesResult = await getAllCompanies();
      if (companiesResult.success) {
        setCompanies(companiesResult.companies);
      }

      // Load profile
      if (currentUser) {
        const result = await getUserProfile(currentUser.uid);
        if (result.success && result.profile) {
          setFormData({
            name: result.profile.name || currentUser.displayName || '',
            email: result.profile.email || currentUser.email || '',
            phone: result.profile.phone || '',
            position: result.profile.position || '',
            companyId: result.profile.companyId || '',
            customCompanyName: result.profile.customCompanyName || ''
          });
          // Check if using custom company name
          if (result.profile.customCompanyName) {
            setUseCustomCompany(true);
          }
        } else {
          setFormData(prev => ({
            ...prev,
            name: currentUser.displayName || '',
            email: currentUser.email || ''
          }));
        }
      }
    };

    loadData();
  }, [currentUser]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Validate that either a company is selected or custom name is provided
    if (!useCustomCompany && !formData.companyId) {
      setMessage('Please select a company or enter a custom company name.');
      setLoading(false);
      return;
    }

    if (useCustomCompany && !formData.customCompanyName.trim()) {
      setMessage('Please enter a company name.');
      setLoading(false);
      return;
    }

    const profileData = {
      ...formData,
      userId: currentUser.uid,
      profileImage: currentUser.photoURL || 'https://via.placeholder.com/150',
      // Clear the field that's not being used
      companyId: useCustomCompany ? '' : formData.companyId,
      customCompanyName: useCustomCompany ? formData.customCompanyName : ''
    };

    const result = await updateUserProfile(currentUser.uid, profileData);

    if (result.success) {
      setMessage('Profile updated successfully!');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } else {
      setMessage('Error updating profile. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-xl shadow-2xl p-8 border-t-4 border-purple-500">
          <div className="flex items-center mb-6">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-xl mr-4">
              <span className="text-3xl">💼</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Employer Profile
              </h1>
              <p className="text-gray-500">Manage your professional information</p>
            </div>
          </div>

          {message && (
            <div className={`mb-6 p-4 rounded-lg shadow-md ${
              message.includes('success')
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 text-green-700'
                : 'bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500 text-red-700'
            }`}>
              <div className="flex items-center">
                <span className="text-2xl mr-3">{message.includes('success') ? '✅' : '❌'}</span>
                <span className="font-medium">{message}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information Section */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">📋</span>
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    placeholder="+1234567890"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Position/Title *
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    placeholder="e.g., HR Manager, Recruiter"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Company Information Section */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-l-4 border-blue-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">🏢</span>
                Company Information
              </h3>

              {/* Toggle between selecting and typing company name */}
              <div className="mb-4">
                <div className="flex items-center space-x-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setUseCustomCompany(false)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      !useCustomCompany
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    📋 Select from List
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseCustomCompany(true)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      useCustomCompany
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    ✍️ Enter Custom Name
                  </button>
                </div>

                {!useCustomCompany ? (
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Select Company *
                    </label>
                    <select
                      name="companyId"
                      value={formData.companyId}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      required={!useCustomCompany}
                    >
                      <option value="">Choose a company from the list</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-sm text-gray-500 mt-2">
                      💡 Select the company you represent from existing companies
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="customCompanyName"
                      value={formData.customCompanyName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="e.g., Acme Corporation"
                      required={useCustomCompany}
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      💡 Enter your company name if it's not in the list
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Benefits Section */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-l-4 border-green-500">
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                <span className="text-xl mr-2">🎁</span>
                Employer Benefits
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start text-gray-700">
                  <span className="text-green-500 mr-3 text-xl flex-shrink-0">✓</span>
                  <span>Access to qualified candidates</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <span className="text-green-500 mr-3 text-xl flex-shrink-0">✓</span>
                  <span>AI-powered candidate matching</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <span className="text-green-500 mr-3 text-xl flex-shrink-0">✓</span>
                  <span>Post and manage job listings</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <span className="text-green-500 mr-3 text-xl flex-shrink-0">✓</span>
                  <span>Review candidate profiles and applications</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col md:flex-row gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin mr-2">⏳</span>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    💾 Save Profile
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white py-4 rounded-xl font-bold hover:from-gray-500 hover:to-gray-600 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                ← Back to Dashboard
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmployerProfilePage;
